import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { handleUppercaseInput } from "@/lib/utils";
import { API, BACKEND_URL, apiFetch } from "@/lib/api";
import { exportRowsToCSV } from "@/lib/csvExport";
import { toLocalISODate } from "@/lib/dateUtils";
import { useAuth } from "@/context/AuthContext";
import { statusConfig, issuePriorityConfig, issueStatusConfig, issueCategoryConfig } from "@/lib/uiConfig";
import DTCManagementSection from "@/components/DTCManagementSection";
import WorkHoldManager from "@/components/WorkHoldManager";
import TimeTracker from "@/components/TimeTracker";
import JobCardStatusWorkflow from "@/components/JobCardStatusWorkflow";
import AddVehicleDataForm from "@/components/forms/AddVehicleDataForm";
import SimpleAddPartForm from "@/components/forms/SimpleAddPartForm";
import MasterPartForm from "@/components/forms/MasterPartForm";
import { useFormDraft, DraftRestoredBar } from "@/components/FormDraft";
import VehicleCascadeDropdowns from "@/components/VehicleCascadeDropdowns";
import {
  Plus, Car, Wrench, AlertTriangle, CheckCircle, Clock, Package,
  Search, Bell, Settings, Users, Calendar, FileText, BarChart3,
  Home, Archive, DollarSign, Phone, Mail, MapPin, Menu, X,
  AlertCircle, Bug, Zap, Settings2, Gauge, Cog, Calculator,
  Eye, Download, Camera, Edit, Trash, RefreshCw, LogOut, LogIn,
  UserPlus, Shield, PackageX, Trash2, Play, List, Clipboard, Database, Minus,
  Check, CreditCard, Truck, Send, Code, FileCheck, Activity,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, Loader2,
  Filter, Info, ClipboardList, ChevronDown
} from "lucide-react";

import { AddPartToPurchaseOrderForm } from "@/pages/shared/ItemForms";


const AddExistingPartToPurchaseOrderForm = ({ parts = [], orderItems = [], onPartsSelected, onCancel }) => {
  // Cascading dropdown state
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSeries, setSelectedSeries] = useState('');
  
  // Vehicle data from curated database
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);
  
  const [vehicleFilters, setVehicleFilters] = useState({
    make: 'all-makes',
    model: 'all-models',
    series: '',
    year: 'all-years'
  });
  const [categoryFilter, setCategoryFilter] = useState('all-categories');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParts, setSelectedParts] = useState([]);
  const [filteredParts, setFilteredParts] = useState(parts);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  // Fetch vehicle makes from curated database
  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(m => m && m.trim() !== '');
        setAvailableMakes(makes);
      } catch (error) {
        console.error('Error fetching vehicle makes:', error);
        setAvailableMakes([]);
      }
    };
    fetchMakes();
  }, []);

  // Fetch models when make changes
  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedMake) {
        setAvailableModels([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(selectedMake)}`);
        const models = (response.data.models || []).filter(m => m && m.trim() !== '');
        setAvailableModels(models);
      } catch (error) {
        console.error('Error fetching vehicle models:', error);
        setAvailableModels([]);
      }
    };
    fetchModels();
  }, [selectedMake]);

  // Fetch series when make+model changes
  useEffect(() => {
    const fetchSeries = async () => {
      if (!selectedMake || !selectedModel) {
        setAvailableSeries([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(selectedMake)}/${encodeURIComponent(selectedModel)}`);
        const series = (response.data.series || []).filter(s => s && s.trim() !== '');
        setAvailableSeries(series);
      } catch (error) {
        console.error('Error fetching vehicle series:', error);
        setAvailableSeries([]);
      }
    };
    fetchSeries();
  }, [selectedMake, selectedModel]);

  // Get unique values for vehicle compatibility filters (still needed for year filter)
  const getUniqueValues = (field) => {
    const values = new Set();
    parts.forEach(part => {
      if (field === 'year' && part.compatible_years) {
        part.compatible_years.forEach(year => values.add(year.toString()));
      }
    });
    return Array.from(values).sort();
  };

  const uniqueYears = getUniqueValues('year');

  const partCategories = [
    'engine', 'transmission', 'brakes', 'suspension', 'electrical', 'body',
    'interior', 'exhaust', 'cooling', 'fuel_system', 'ac_system', 'filters',
    'oils_fluids', 'tires', 'others'
  ];
  const categoryLabel = (cat) => cat.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  // Enhanced search function with intelligent matching
  const searchParts = (parts, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return parts;
    }

    const normalizedSearchTerm = searchTerm.toLowerCase().trim();
    const searchWords = normalizedSearchTerm.split(/\s+/).filter(word => word.length > 0);

    return parts.filter(part => {
      // Create searchable text from all part fields
      const searchableFields = [
        part.part_name || '',
        part.part_number || '',
        part.brand || '',
        part.description || '',
        part.category || '',
        part.supplier || '',
        part.location || '',
        // Include vehicle compatibility in search
        ...(part.compatible_makes || []),
        ...(part.compatible_models || []),
        ...(part.compatible_years || []).map(year => year.toString())
      ].join(' ').toLowerCase();

      // Check if all search words are found (AND logic)
      const allWordsFound = searchWords.every(word => {
        return searchableFields.includes(word) || 
               searchableFields.includes(word.replace(/s$/, '')) || // Handle plurals
               searchableFields.split(/\s+/).some(field => 
                 field.startsWith(word) || // Partial word matching
                 field.includes(word)
               );
      });

      // Exact part number match (highest priority)
      const exactPartNumberMatch = (part.part_number || '').toLowerCase() === normalizedSearchTerm;
      
      // Exact part name match (high priority)
      const exactPartNameMatch = (part.part_name || '').toLowerCase() === normalizedSearchTerm;
      
      // Brand match
      const brandMatch = (part.brand || '').toLowerCase().includes(normalizedSearchTerm);
      
      // Category match
      const categoryMatch = (part.category || '').toLowerCase().includes(normalizedSearchTerm);

      return exactPartNumberMatch || exactPartNameMatch || brandMatch || categoryMatch || allWordsFound;
    }).filter(Boolean); // Remove any undefined/null results
  };

  // Get search suggestions based on current parts
  const getSearchSuggestions = () => {
    const suggestions = new Set();
    
    parts.forEach(part => {
      // Add part names
      if (part.part_name) {
        suggestions.add(part.part_name);
        // Add individual words from part names
        part.part_name.split(' ').forEach(word => {
          if (word.length > 2) suggestions.add(word);
        });
      }
      
      // Add brands
      if (part.brand) suggestions.add(part.brand);
      
      // Add categories
      if (part.category) suggestions.add(part.category.replace('_', ' '));
      
      // Add vehicle makes/models
      if (part.compatible_makes) {
        part.compatible_makes.forEach(make => suggestions.add(make));
      }
      if (part.compatible_models) {
        part.compatible_models.forEach(model => suggestions.add(model));
      }
    });

    return Array.from(suggestions).slice(0, 10); // Limit suggestions
  };

  // Filter parts based on vehicle compatibility and search term
  useEffect(() => {
    if (!parts || !Array.isArray(parts)) {
      setFilteredParts([]);
      return;
    }
    
    let filtered = parts.filter(part => {
      // Already added to order check
      const alreadyAdded = orderItems.some(item => item.part_id === part.id);
      if (alreadyAdded) return false;

      // Vehicle compatibility filters.
      // Lenient rule (matches Invoice/Quotation): a part with NO compatibility data
      // shows under any filter; a part WITH data is excluded only when it doesn't match.
      if (vehicleFilters.make && vehicleFilters.make !== 'all-makes') {
        const hasMakeData = Array.isArray(part.compatible_makes) && part.compatible_makes.length > 0;
        if (hasMakeData && !part.compatible_makes.includes(vehicleFilters.make)) {
          return false;
        }
      }

      if (vehicleFilters.model && vehicleFilters.model !== 'all-models') {
        const hasModelData = Array.isArray(part.compatible_models) && part.compatible_models.length > 0;
        if (hasModelData && !part.compatible_models.includes(vehicleFilters.model)) {
          return false;
        }
      }

      if (vehicleFilters.year && vehicleFilters.year !== 'all-years') {
        const hasYearData = Array.isArray(part.compatible_years) && part.compatible_years.length > 0;
        if (hasYearData && !part.compatible_years.includes(parseInt(vehicleFilters.year))) {
          return false;
        }
      }

      // Series filter — match against compatible_series (lenient)
      if (vehicleFilters.series && vehicleFilters.series.trim() && vehicleFilters.series !== 'all-series') {
        const hasSeriesData = Array.isArray(part.compatible_series) && part.compatible_series.length > 0;
        if (hasSeriesData && !part.compatible_series.includes(vehicleFilters.series)) {
          return false;
        }
      }

      // Category filter
      if (categoryFilter && categoryFilter !== 'all-categories' && part.category !== categoryFilter) {
        return false;
      }

      return true;
    });

    // Apply enhanced search
    if (searchTerm && searchTerm.trim()) {
      filtered = searchParts(filtered, searchTerm);
    }

    setFilteredParts(filtered);
  }, [parts, vehicleFilters, categoryFilter, searchTerm, orderItems]);

  // Initialize search suggestions
  useEffect(() => {
    const suggestions = getSearchSuggestions();
    setSearchSuggestions(suggestions);
  }, [parts]);

  const handleVehicleFilterChange = (field, value) => {
    setVehicleFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearchInputChange = (value) => {
    setSearchTerm(value);
    setShowSearchSuggestions(value.length > 0);
  };

  const handleSearchSuggestionSelect = (suggestion) => {
    setSearchTerm(suggestion);
    setShowSearchSuggestions(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setShowSearchSuggestions(false);
  };

  const buildPartItem = (part, brand) => {
    const b = brand || (Array.isArray(part.brands) && part.brands.length > 0 ? part.brands[0] : null);
    const cost = b ? (b.cost_price || 0) : (part.cost_price || 0);
    return {
      id: Date.now() + Math.random(),
      part_id: part.id,
      part_name: part.part_name,
      part_number: b ? (b.part_number || '') : (part.part_number || ''),
      brand_id: b ? b.id : null,
      brand_name: b ? b.brand_name : null,
      quantity: 1,
      unit_cost: cost,
      total_cost: cost,
    };
  };

  const updateSelectedBrand = (part, brandId) => {
    const b = (part.brands || []).find((x) => x.id === brandId);
    setSelectedParts((prev) => prev.map((item) => {
      if (item.part_id !== part.id) return item;
      const cost = b ? (b.cost_price || 0) : item.unit_cost;
      return {
        ...item,
        brand_id: b ? b.id : item.brand_id,
        brand_name: b ? b.brand_name : item.brand_name,
        part_number: b ? (b.part_number || '') : item.part_number,
        unit_cost: cost,
        total_cost: cost * (item.quantity || 1),
      };
    }));
  };

  const handlePartSelection = (part, isSelected) => {
    if (isSelected) {
      setSelectedParts([...selectedParts, buildPartItem(part)]);
    } else {
      setSelectedParts(selectedParts.filter(item => item.part_id !== part.id));
    }
  };

  const clearFilters = () => {
    setSelectedMake('');
    setSelectedModel('');
    setSelectedSeries('');
    setVehicleFilters({
      make: 'all-makes',
      model: 'all-models', 
      series: '',
      year: 'all-years'
    });
    setCategoryFilter('all-categories');
    clearSearch();
  };

  // Function to highlight search terms in text
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const searchWords = searchTerm.toLowerCase().split(/\s+/).filter(word => word.length > 0);
    let highlightedText = text;
    
    searchWords.forEach(word => {
      const regex = new RegExp(`(${word})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
    });
    
    return highlightedText;
  };

  const handleSubmit = () => {
    if (selectedParts.length === 0) {
      toast.error('Please select at least one part');
      return;
    }
    onPartsSelected(selectedParts);
  };

  const handleInlinePartCreated = (savedPart) => {
    onPartsSelected([buildPartItem(savedPart)]);
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Add Existing Parts</DialogTitle>
        <DialogDescription>
          Find and add compatible parts to your purchase order
        </DialogDescription>
      </DialogHeader>

      {/* Vehicle Compatibility Filters */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-medium text-gray-900">🚗 Vehicle Compatibility Filters</h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="text-gray-600 hover:text-gray-700"
          >
            Clear Filters
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* MAKE Cascading Dropdown */}
          <div>
            <Label htmlFor="filter_make">MAKE</Label>
            <Select 
              value={selectedMake || 'all-makes'} 
              onValueChange={(value) => {
                if (value === 'all-makes') {
                  setSelectedMake('');
                  setSelectedModel('');
                  setSelectedSeries('');
                  setVehicleFilters(prev => ({ ...prev, make: 'all-makes', model: 'all-models', series: '' }));
                } else {
                  setSelectedMake(value);
                  setSelectedModel('');
                  setSelectedSeries('');
                  setVehicleFilters(prev => ({ ...prev, make: value, model: 'all-models', series: '' }));
                }
              }}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All Makes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-makes">All Makes</SelectItem>
                {availableMakes.map((make) => (
                  <SelectItem key={make} value={make}>{make}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* MODEL Cascading Dropdown */}
          <div>
            <Label htmlFor="filter_model">MODEL</Label>
            <Select 
              value={selectedModel || 'all-models'} 
              onValueChange={(value) => {
                if (value === 'all-models') {
                  setSelectedModel('');
                  setSelectedSeries('');
                  setVehicleFilters(prev => ({ ...prev, model: 'all-models', series: '' }));
                } else {
                  setSelectedModel(value);
                  setSelectedSeries('');
                  setVehicleFilters(prev => ({ ...prev, model: value, series: '' }));
                }
              }}
              disabled={!selectedMake}
            >
              <SelectTrigger className="mt-1">
                <SelectValue 
                  placeholder={
                    selectedMake 
                      ? `Select ${selectedMake} MODEL`
                      : "Select MAKE first"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-models">
                  {selectedMake ? `All ${selectedMake} Models` : 'All Models'}
                </SelectItem>
                {availableModels.map((model) => (
                  <SelectItem key={model} value={model}>
                    {model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedMake && (
              <div className="text-xs text-gray-500 mt-1">
                {availableModels.length > 0 
                  ? `${availableModels.length} ${selectedMake} MODELs available`
                  : `No predefined models for ${selectedMake}`
                }
              </div>
            )}
          </div>

          {/* SERIES Cascading Dropdown */}
          <div>
            <Label htmlFor="filter_series">SERIES</Label>
            <Select
              value={selectedSeries || 'all-series'}
              onValueChange={(value) => {
                if (value === 'all-series') {
                  setSelectedSeries('');
                  setVehicleFilters(prev => ({ ...prev, series: '' }));
                } else {
                  setSelectedSeries(value);
                  setVehicleFilters(prev => ({ ...prev, series: value }));
                }
              }}
              disabled={!selectedMake || !selectedModel}
            >
              <SelectTrigger className="mt-1">
                <SelectValue 
                  placeholder={
                    !selectedMake ? "Select MAKE first" :
                    !selectedModel ? "Select MODEL first" :
                    `Select ${selectedModel} SERIES`
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-series">
                  {selectedModel ? `All ${selectedModel} Series` : 'All Series'}
                </SelectItem>
                {availableSeries.map((series) => (
                  <SelectItem key={series} value={series}>
                    {series}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedMake && selectedModel && (
              <div className="text-xs text-gray-500 mt-1">
                {availableSeries.length > 0 
                  ? `${availableSeries.length} ${selectedModel} SERIES available`
                  : `No predefined series for ${selectedModel}`
                }
              </div>
            )}
            {!selectedMake || !selectedModel ? (
              <p className="text-xs text-gray-500 mt-1">Select MAKE and MODEL first to see SERIES options</p>
            ) : null}
          </div>

          {/* Year Filter (unchanged) */}
          <div>
            <Label htmlFor="filter_year">YEAR</Label>
            <Select 
              value={vehicleFilters.year} 
              onValueChange={(value) => handleVehicleFilterChange('year', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-years">All Years</SelectItem>
                {uniqueYears.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category Filter */}
          <div>
            <Label htmlFor="filter_category">CATEGORY</Label>
            <Select
              value={categoryFilter}
              onValueChange={setCategoryFilter}
            >
              <SelectTrigger className="mt-1" data-testid="po-existing-filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-categories">All Categories</SelectItem>
                {partCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{categoryLabel(cat)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Enhanced Search Bar */}
      <div>
        <Label htmlFor="search">Search Parts</Label>
        <div className="relative mt-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            id="search"
            placeholder="Search by part name, number, brand, category, or vehicle compatibility..."
            defaultValue={searchTerm}
            onInput={(e) => handleSearchInputChange(e.target.value)}
            onFocus={(e) => setShowSearchSuggestions((e.target.value || '').length > 0)}
            className="pl-8 pr-8"
            data-testid="po-existing-parts-search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                clearSearch();
                const inp = document.getElementById('search');
                if (inp) inp.value = '';
              }}
              className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
          
          {/* Search Suggestions Dropdown */}
          {showSearchSuggestions && searchSuggestions.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
              <div className="p-2 text-xs text-gray-500 border-b">
                Search suggestions:
              </div>
              {searchSuggestions
                .filter(suggestion => 
                  suggestion.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .slice(0, 8)
                .map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSearchSuggestionSelect(suggestion)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                  >
                    <Search className="inline h-3 w-3 mr-2 text-gray-400" />
                    {suggestion}
                  </button>
                ))
              }
            </div>
          )}
        </div>
        
        {/* Search Help Text */}
        <div className="mt-1 text-xs text-gray-500">
          <strong>Search tips:</strong> Try "brake pad", "NGK", "engine", "Honda Civic", or part numbers like "OF001"
        </div>
      </div>

      {/* Search Results Summary */}
      {(searchTerm || Object.values(vehicleFilters).some(v => v && v !== 'all-makes' && v !== 'all-models' && v !== 'all-years')) && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="font-medium text-blue-900">Search Results</h4>
              <p className="text-sm text-blue-700">
                Found {filteredParts.length} part(s) matching your criteria
                {searchTerm && ` for "${searchTerm}"`}
              </p>
            </div>
            {filteredParts.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const allParts = filteredParts.map(part => buildPartItem(part));
                  setSelectedParts([...selectedParts, ...allParts]);
                  toast.success(`Added ${allParts.length} parts to selection!`);
                }}
                className="text-blue-700 border-blue-300"
              >
                Add All Results
              </Button>
            )}
          </div>
        </Card>
      )}
      <div className="flex flex-wrap gap-2 text-sm text-gray-600">
        {vehicleFilters.make && vehicleFilters.make !== 'all-makes' && (
          <Badge variant="outline">Make: {vehicleFilters.make}</Badge>
        )}
        {vehicleFilters.model && vehicleFilters.model !== 'all-models' && (
          <Badge variant="outline">Model: {vehicleFilters.model}</Badge>
        )}
        {vehicleFilters.series && (
          <Badge variant="outline">Series: {vehicleFilters.series}</Badge>
        )}
        {vehicleFilters.year && vehicleFilters.year !== 'all-years' && (
          <Badge variant="outline">Year: {vehicleFilters.year}</Badge>
        )}
        {searchTerm && (
          <Badge variant="outline">Search: "{searchTerm}"</Badge>
        )}
      </div>

      {/* Parts List */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-medium text-gray-900">
            Available Parts ({filteredParts.length})
          </h4>
          <p className="text-sm text-gray-600">
            Selected: {selectedParts.length} part(s)
          </p>
        </div>

        {filteredParts.length === 0 ? (
          <Card className="p-6 text-center text-gray-500">
            <Package className="h-8 w-8 mx-auto mb-2" />
            <h3 className="font-semibold mb-1">No Compatible Parts Found</h3>
            <p className="mb-2">
              {Object.values(vehicleFilters).some(v => v) || searchTerm
                ? "Try adjusting your filters or search criteria"
                : "No parts available to add to purchase order"
              }
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Total parts in database: {parts?.length || 0}
              {searchTerm && ` | Search term: "${searchTerm}"`}
              {Object.values(vehicleFilters).some(v => v) && ' | Filters active'}
            </p>
            {(Object.values(vehicleFilters).some(v => v) || searchTerm) && (
              <Button variant="outline" size="sm" onClick={clearFilters} className="mt-2">
                Clear All Filters
              </Button>
            )}
            {!showCreateForm && (
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={() => setShowCreateForm(true)} data-testid="empty-create-part-btn">
                  + Create New Part
                </Button>
              </div>
            )}
          </Card>
        ) : (
          <div className="max-h-96 overflow-y-auto border rounded-lg">
            <div className="grid gap-2 p-4">
              {filteredParts.map((part) => {
                const isSelected = selectedParts.some(item => item.part_id === part.id);
                return (
                  <Card key={part.id} className={`p-4 cursor-pointer transition-colors ${
                    isSelected ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div 
                        className="flex-1 mr-4"
                        onClick={() => handlePartSelection(part, !isSelected)}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <h5 
                            className="font-semibold"
                            dangerouslySetInnerHTML={{ 
                              __html: highlightSearchTerm(part.part_name, searchTerm) 
                            }}
                          />
                          <Badge variant="outline" className="text-xs">
                            <span dangerouslySetInnerHTML={{ 
                              __html: highlightSearchTerm(part.part_number, searchTerm) 
                            }} />
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm text-gray-600 mb-2">
                          <div>
                            <span className="font-medium">Brand:</span>{' '}
                            <span dangerouslySetInnerHTML={{ 
                              __html: highlightSearchTerm(part.brand || 'N/A', searchTerm) 
                            }} />
                          </div>
                          <div>
                            <span className="font-medium">Category:</span>{' '}
                            <span dangerouslySetInnerHTML={{ 
                              __html: highlightSearchTerm(part.category || 'N/A', searchTerm) 
                            }} />
                          </div>
                          <div>
                            <span className="font-medium">Stock:</span> {part.stock_quantity || 0}
                          </div>
                          <div>
                            <span className="font-medium text-green-600">Cost:</span> ₹{part.cost_price?.toFixed(2) || '0.00'}
                          </div>
                        </div>

                        {/* Vehicle Compatibility Display */}
                        {(part.compatible_makes?.length || part.compatible_models?.length || part.compatible_years?.length) && (
                          <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                            <span className="font-medium">Compatible with:</span>{' '}
                            {part.compatible_makes?.join(', ')} {part.compatible_models?.join(', ')} {part.compatible_years?.join(', ')}
                          </div>
                        )}

                        {part.description && (
                          <p className="text-sm text-gray-500 mt-2">{part.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Brand selector (per-brand stock is maintained separately) */}
                    {isSelected && Array.isArray(part.brands) && part.brands.length > 0 && (
                      <div className="mt-3 pl-7" onClick={(e) => e.stopPropagation()} data-testid={`po-brand-select-wrap-${part.id}`}>
                        <Label className="text-xs">Brand to purchase *</Label>
                        <Select
                          value={selectedParts.find((i) => i.part_id === part.id)?.brand_id || ''}
                          onValueChange={(v) => updateSelectedBrand(part, v)}
                        >
                          <SelectTrigger className="h-9 w-full md:w-96" data-testid={`po-brand-select-${part.id}`}>
                            <SelectValue placeholder="Select brand" />
                          </SelectTrigger>
                          <SelectContent>
                            {part.brands.map((b) => (
                              <SelectItem key={b.id} value={b.id}>
                                {b.brand_name}{b.part_number ? ` — ${b.part_number}` : ''} · Stock {b.stock_quantity ?? 0} · ₹{(b.cost_price ?? 0)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showCreateForm && (
        <div className="border rounded-md p-4 bg-gray-50 space-y-3" data-testid="inline-create-part-wrapper">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Create New Part</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>
              Cancel
            </Button>
          </div>
          <MasterPartForm
            onSuccess={handleInlinePartCreated}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-between items-center pt-4 border-t">
        <p className="text-sm text-gray-600">
          {selectedParts.length > 0 && (
            <>Total cost: ₹{selectedParts.reduce((sum, part) => sum + part.total_cost, 0).toFixed(2)}</>
          )}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button 
            type="button" 
            onClick={handleSubmit}
            disabled={selectedParts.length === 0}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
          >
            Add {selectedParts.length} Part{selectedParts.length !== 1 ? 's' : ''} to Order
          </Button>
        </div>
      </div>
    </div>
  );
};

// Add Part Form Component for Purchase Orders

const AddPurchaseOrderForm = ({ suppliers, parts, onSuccess, onCancel, onPartCreated }) => {
  const [orderItems, setOrderItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddPartDialog, setShowAddPartDialog] = useState(false);
  const [showAddExistingPartDialog, setShowAddExistingPartDialog] = useState(false);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [purchaseType, setPurchaseType] = useState('gst_bill');

  // Use React Hook Form for proper state management
  const { 
    register, 
    handleSubmit, 
    control, 
    formState: { errors },
    reset,
    setValue,
    watch 
  } = useForm({
    defaultValues: {
      supplier_id: '',
      order_date: new Date().toISOString().split('T')[0],
      expected_delivery_date: '',
      notes: ''
    }
  });

  // Watch supplier selection for validation
  const watchSupplier = watch('supplier_id');

  // Autosave draft (create only)
  const pendingSupplierRef = useRef(null);
  const poRhfValues = watch();
  const poDraftHasContent =
    orderItems.length > 0 ||
    (poRhfValues.supplier_id || '') !== '' ||
    (poRhfValues.notes || '').trim() !== '' ||
    (poRhfValues.expected_delivery_date || '') !== '' ||
    parseFloat(shippingCharge) > 0;
  const { draftRestored, clearDraft, dismissBanner } = useFormDraft({
    storageKey: 'draft:purchase-order-create',
    hasContent: poDraftHasContent,
    data: { rhf: poRhfValues, orderItems, shippingCharge },
    onRestore: (d) => {
      if (d.rhf) reset(d.rhf);
      if (Array.isArray(d.orderItems)) setOrderItems(d.orderItems);
      if (d.shippingCharge !== undefined && d.shippingCharge !== null) setShippingCharge(d.shippingCharge);
      // Re-apply supplier once the suppliers list is available (Radix Select
      // needs the matching option mounted to show its label).
      if (d.rhf && d.rhf.supplier_id) pendingSupplierRef.current = d.rhf.supplier_id;
    },
  });

  // When the suppliers list loads, re-apply a restored supplier_id so the
  // Select visually reflects the draft value.
  useEffect(() => {
    if (pendingSupplierRef.current && suppliers && suppliers.length > 0) {
      setValue('supplier_id', pendingSupplierRef.current, { shouldValidate: false, shouldDirty: true });
      pendingSupplierRef.current = null;
    }
  }, [suppliers, setValue]);
  const discardDraft = () => {
    clearDraft();
    reset({ supplier_id: '', order_date: new Date().toISOString().split('T')[0], expected_delivery_date: '', notes: '' });
    setOrderItems([]);
    setShippingCharge(0);
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      part_id: '',
      part_name: '',
      part_number: '',
      quantity: 1,
      unit_cost: 0,
      total_cost: 0
    };
    setOrderItems([...orderItems, newItem]);
  };

  const removeItem = (itemId) => {
    setOrderItems(orderItems.filter(item => item.id !== itemId));
  };

  const updateItem = (itemId, field, value) => {
    setOrderItems(orderItems.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        
        // If part is selected, auto-fill details (default to the first brand)
        if (field === 'part_id' && value) {
          const selectedPart = parts.find(p => p.id === value);
          if (selectedPart) {
            const b = Array.isArray(selectedPart.brands) && selectedPart.brands.length > 0 ? selectedPart.brands[0] : null;
            updatedItem.part_name = selectedPart.part_name;
            updatedItem.part_number = b ? (b.part_number || '') : selectedPart.part_number;
            updatedItem.brand_id = b ? b.id : null;
            updatedItem.brand_name = b ? b.brand_name : null;
            updatedItem.unit_cost = b ? (b.cost_price || 0) : (selectedPart.cost_price || 0);
          }
        }

        // If brand changed on this item, re-fill part_number and unit_cost from that brand
        if (field === 'brand_id' && value) {
          const selectedPart = parts.find(p => p.id === item.part_id);
          const b = selectedPart && (selectedPart.brands || []).find(x => x.id === value);
          if (b) {
            updatedItem.brand_name = b.brand_name;
            updatedItem.part_number = b.part_number || '';
            updatedItem.unit_cost = b.cost_price || 0;
          }
        }
        
        // Calculate total cost (quantity/unit_cost may have changed directly, or
        // indirectly via a part/brand change above — always keep total_cost in sync)
        updatedItem.total_cost = (updatedItem.quantity || 0) * (updatedItem.unit_cost || 0);
        
        return updatedItem;
      }
      return item;
    }));
  };

  const calculateTotal = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + item.total_cost, 0);
    let tax = 0;
    if (purchaseType === 'gst_bill') {
      tax = orderItems.reduce((sum, item) => {
        const part = parts.find(p => p.id === item.part_id);
        const rate = (part && part.gst_rate != null) ? part.gst_rate : 18;
        return sum + (item.total_cost * rate / 100);
      }, 0);
    }
    const shipping = parseFloat(shippingCharge) || 0;
    const total = subtotal + tax + shipping;
    return { subtotal, tax, shipping, total };
  };

  const { subtotal, tax, shipping, total } = calculateTotal();

  const handlePartAdded = async (newPart) => {
    try {
      // Refresh parts list in parent so the newly created part appears in
      // the "Add Existing Part" dropdown without needing to close/reopen the dialog.
      if (onPartCreated) {
        onPartCreated(newPart);
      }
      
      // Verify the part was actually created by fetching it
      const verifyResponse = await apiFetch(`${API}/parts/${newPart.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!verifyResponse.ok) {
        toast.error('Warning: Part creation may have failed. Please check Parts & Inventory.');
        return;
      }
      
      // Automatically add the new part as an item to the current purchase order
      const nb = Array.isArray(newPart.brands) && newPart.brands.length > 0 ? newPart.brands[0] : null;
      const newItem = {
        id: Date.now(),
        part_id: newPart.id,
        part_name: newPart.part_name,
        part_number: nb ? (nb.part_number || '') : newPart.part_number,
        brand_id: nb ? nb.id : null,
        brand_name: nb ? nb.brand_name : null,
        quantity: 1,
        unit_cost: nb ? (nb.cost_price || 0) : (newPart.cost_price || 0),
        total_cost: nb ? (nb.cost_price || 0) : (newPart.cost_price || 0)
      };
      
      setOrderItems([...orderItems, newItem]);
      setShowAddPartDialog(false);
      
      toast.success(`${newPart.part_name} created and added to purchase order!`);
      
    } catch (error) {
      console.error('Error in handlePartAdded:', error);
      toast.error('Part created but there was an issue adding it to the purchase order');
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!data.supplier_id) {
        toast.error("Please select a supplier");
        setIsSubmitting(false);
        return;
      }

      if (orderItems.length === 0) {
        toast.error("Please add at least one item to the purchase order");
        setIsSubmitting(false);
        return;
      }

      const orderData = {
        supplier_id: data.supplier_id,
        order_date: data.order_date || null,
        expected_delivery_date: data.expected_delivery_date || null,
        items: orderItems.map(item => ({
          part_id: item.part_id,
          part_name: item.part_name,
          part_number: item.part_number,
          brand_id: item.brand_id || null,
          brand_name: item.brand_name || null,
          quantity: parseFloat(item.quantity),
          unit_cost: parseFloat(item.unit_cost),
          total_cost: parseFloat(item.total_cost)
        })),
        notes: data.notes?.trim() || '',
        shipping_charge: parseFloat(shippingCharge) || 0,
        purchase_type: purchaseType
      };

      const response = await apiFetch(`${API}/purchase-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        toast.success('Purchase order created successfully!');
        clearDraft();
        reset(); // Reset form
        setOrderItems([]); // Clear order items
        onSuccess();
      } else {
        const error = await response.text();
        toast.error('Failed to create purchase order: ' + error);
      }
    } catch (error) {
      console.error('Error creating purchase order:', error);
      toast.error('Failed to create purchase order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormError = (errors) => {
    console.log('Form validation errors:', errors);
    const firstError = Object.values(errors)[0];
    if (firstError) {
      toast.error(firstError.message || 'Please check the form fields');
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, handleFormError)} className="space-y-6">
        {draftRestored && <DraftRestoredBar onDiscard={discardDraft} onDismiss={dismissBanner} />}
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="supplier_id">Supplier *</Label>
          <Controller
            name="supplier_id"
            control={control}
            rules={{ required: "Please select a supplier" }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.supplier_id && (
            <p className="text-red-500 text-sm mt-1">{errors.supplier_id.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="order_date">Order Date *</Label>
          <Input
            id="order_date"
            type="date"
            {...register("order_date", { required: "Please select an order date" })}
            data-testid="po-order-date-input"
          />
          {errors.order_date && (
            <p className="text-red-500 text-sm mt-1">{errors.order_date.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="expected_delivery_date">Expected Delivery Date</Label>
          <Input
            id="expected_delivery_date"
            type="date"
            {...register("expected_delivery_date")}
          />
        </div>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50">
        <div>
          <Label htmlFor="po-purchase-type" className="font-medium text-gray-700">GST Bill Available</Label>
          <p className="text-xs text-gray-500">On = supplier gave a proper GST invoice (ITC claimable, tax added to total). Off = local/unbilled purchase.</p>
        </div>
        <Switch
          id="po-purchase-type"
          data-testid="po-purchase-type-switch"
          checked={purchaseType === 'gst_bill'}
          onCheckedChange={(v) => setPurchaseType(v ? 'gst_bill' : 'non_gst')}
        />
      </div>

      {/* Order Items */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <Label>Order Items</Label>
          <div className="flex gap-2">
            <Button type="button" onClick={() => setShowAddPartDialog(true)} variant="outline" size="sm" className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300">
              <Plus className="h-4 w-4 mr-1" />
              Add New Part
            </Button>
            <Button type="button" onClick={() => setShowAddExistingPartDialog(true)} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add Existing Part
            </Button>
          </div>
        </div>

        {orderItems.length === 0 ? (
          <Card className="p-6 text-center text-gray-500">
            <Package className="h-8 w-8 mx-auto mb-2" />
            <p>No items added yet. Click "Add Item" to get started.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {orderItems.map((item, index) => (
              <Card key={item.id} className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                  <div className="md:col-span-2">
                    <Label>Part</Label>
                    <Select
                      value={item.part_id}
                      onValueChange={(value) => updateItem(item.id, 'part_id', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select part" />
                      </SelectTrigger>
                      <SelectContent>
                        {parts.map((part) => (
                          <SelectItem key={part.id} value={part.id}>
                            {part.part_name} - {part.part_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(() => {
                      const selPart = parts.find((p) => p.id === item.part_id);
                      const pbrands = (selPart && selPart.brands) || [];
                      if (pbrands.length === 0) return null;
                      return (
                        <div className="mt-2">
                          <Label className="text-xs">Brand</Label>
                          <Select
                            value={item.brand_id || ''}
                            onValueChange={(value) => updateItem(item.id, 'brand_id', value)}
                          >
                            <SelectTrigger className="h-9" data-testid={`po-item-brand-select-${item.id}`}>
                              <SelectValue placeholder="Select brand">
                                {(pbrands.find((b) => b.id === item.brand_id)?.brand_name) || item.brand_name || 'Select brand'}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {pbrands.map((b) => (
                                <SelectItem key={b.id} value={b.id}>
                                  {b.brand_name}{b.part_number ? ` — ${b.part_number}` : ''} · Stock {b.stock_quantity ?? 0}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })()}
                  </div>

                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 1)}
                    />
                  </div>

                  <div>
                    <Label>Unit Cost (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.unit_cost}
                      onChange={(e) => updateItem(item.id, 'unit_cost', parseFloat(e.target.value) || 0)}
                    />
                  </div>

                  <div>
                    <Label>Total</Label>
                    <Input
                      value={`₹${item.total_cost.toFixed(2)}`}
                      readOnly
                      className="bg-gray-50"
                    />
                  </div>

                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeItem(item.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}

            {/* Order Total */}
            <Card className="p-4 bg-gray-50">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal:</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                {purchaseType === 'gst_bill' && (
                  <div className="flex justify-between text-sm text-gray-600" data-testid="po-estimated-tax">
                    <span>Estimated GST (Input Tax):</span>
                    <span>₹{tax.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-sm">
                  <Label htmlFor="shipping_charge" className="text-gray-600">Shipping Charge (₹):</Label>
                  <Input
                    id="shipping_charge"
                    type="number"
                    min="0"
                    step="0.01"
                    value={shippingCharge}
                    onChange={(e) => setShippingCharge(e.target.value)}
                    className="w-32 h-8 text-right"
                    data-testid="po-shipping-charge-input"
                  />
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span data-testid="po-total-amount">₹{total.toFixed(2)}</span>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          placeholder="Additional notes or requirements..."
          {...register("notes")}
          rows={3}
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting || !watchSupplier || orderItems.length === 0}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating Purchase Order...
            </>
          ) : (
            'Create Purchase Order'
          )}
        </Button>
      </div>
    </form>

    {/* Add Existing Part Dialog */}
    <Dialog open={showAddExistingPartDialog} onOpenChange={setShowAddExistingPartDialog}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <AddExistingPartToPurchaseOrderForm 
          parts={parts}
          orderItems={orderItems}
          onPartsSelected={(selectedParts) => {
            setOrderItems([...orderItems, ...selectedParts]);
            setShowAddExistingPartDialog(false);
            if (onPartCreated) onPartCreated();
            toast.success(`${selectedParts.length} part(s) added to purchase order!`);
          }}
          onCancel={() => setShowAddExistingPartDialog(false)} 
        />
      </DialogContent>
    </Dialog>

    {/* Add Part Dialog */}
    <Dialog open={showAddPartDialog} onOpenChange={setShowAddPartDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Part</DialogTitle>
        </DialogHeader>
        <MasterPartForm
          onSuccess={handlePartAdded}
          onCancel={() => setShowAddPartDialog(false)}
        />
      </DialogContent>
    </Dialog>
    </>
  );
};

//Add Existing Parts Form Component with Vehicle Compatibility Filters

const EditPurchaseOrderForm = ({ order, suppliers, parts, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    supplier_id: order.supplier_id || '',
    order_date: order.order_date ? order.order_date.split('T')[0] : '',
    expected_delivery_date: order.expected_delivery_date ? order.expected_delivery_date.split('T')[0] : '',
    status: order.status || 'draft',
    notes: order.notes || ''
  });
  const [orderItems, setOrderItems] = useState(order.items || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shippingCharge, setShippingCharge] = useState(order.shipping_charge || 0);
  const [purchaseType, setPurchaseType] = useState(order.purchase_type || 'gst_bill');

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      part_id: '',
      part_name: '',
      part_number: '',
      quantity: 1,
      unit_cost: 0,
      total_cost: 0
    };
    setOrderItems([...orderItems, newItem]);
  };

  const removeItem = (itemIndex) => {
    setOrderItems(orderItems.filter((_, index) => index !== itemIndex));
  };

  const updateItem = (itemIndex, field, value) => {
    setOrderItems(orderItems.map((item, index) => {
      if (index === itemIndex) {
        const updatedItem = { ...item, [field]: value };
        
        // If part is selected, auto-fill details (default to the first brand)
        if (field === 'part_id' && value) {
          const selectedPart = parts.find(p => p.id === value);
          if (selectedPart) {
            const b = Array.isArray(selectedPart.brands) && selectedPart.brands.length > 0 ? selectedPart.brands[0] : null;
            updatedItem.part_name = selectedPart.part_name;
            updatedItem.part_number = b ? (b.part_number || '') : selectedPart.part_number;
            updatedItem.brand_id = b ? b.id : null;
            updatedItem.brand_name = b ? b.brand_name : null;
            updatedItem.unit_cost = b ? (b.cost_price || 0) : (selectedPart.cost_price || 0);
          }
        }

        // If brand changed on this item, re-fill part_number and unit_cost from that brand
        if (field === 'brand_id' && value) {
          const selectedPart = parts.find(p => p.id === item.part_id);
          const b = selectedPart && (selectedPart.brands || []).find(x => x.id === value);
          if (b) {
            updatedItem.brand_name = b.brand_name;
            updatedItem.part_number = b.part_number || '';
            updatedItem.unit_cost = b.cost_price || 0;
          }
        }
        
        // Calculate total cost (quantity/unit_cost may have changed directly, or
        // indirectly via a part/brand change above — always keep total_cost in sync)
        updatedItem.total_cost = (updatedItem.quantity || 0) * (updatedItem.unit_cost || 0);
        
        return updatedItem;
      }
      return item;
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const updateData = {
        supplier_id: formData.supplier_id,
        order_date: formData.order_date || null,
        expected_delivery_date: formData.expected_delivery_date || null,
        status: formData.status,
        items: orderItems.map(item => ({
          part_id: item.part_id,
          part_name: item.part_name,
          part_number: item.part_number,
          brand_id: item.brand_id || null,
          brand_name: item.brand_name || null,
          quantity: parseFloat(item.quantity),
          unit_cost: parseFloat(item.unit_cost),
          total_cost: parseFloat(item.total_cost)
        })),
        notes: formData.notes,
        shipping_charge: parseFloat(shippingCharge) || 0,
        purchase_type: purchaseType
      };

      const response = await apiFetch(`${API}/purchase-orders/${order.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.text();
        alert('Failed to update purchase order: ' + error);
      }
    } catch (error) {
      console.error('Error updating purchase order:', error);
      alert('Failed to update purchase order');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateTotal = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + (item.total_cost || 0), 0);
    let tax = 0;
    if (purchaseType === 'gst_bill') {
      tax = orderItems.reduce((sum, item) => {
        const part = parts.find(p => p.id === item.part_id);
        const rate = (part && part.gst_rate != null) ? part.gst_rate : 18;
        return sum + ((item.total_cost || 0) * rate / 100);
      }, 0);
    }
    const shipping = parseFloat(shippingCharge) || 0;
    const total = subtotal + tax + shipping;
    return { subtotal, tax, shipping, total };
  };

  const { subtotal, tax, shipping, total } = calculateTotal();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {order.status === 'delivered' && (
        <div className="rounded-md bg-amber-50 border border-amber-300 text-amber-800 text-sm px-4 py-3" data-testid="po-edit-delivered-warning">
          This order was already marked <strong>Received</strong>. Changing item quantities here will
          automatically adjust the affected parts' stock by the difference.
        </div>
      )}
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="supplier_id">Supplier *</Label>
          <Select
            value={formData.supplier_id}
            onValueChange={(value) => setFormData({...formData, supplier_id: value})}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select supplier" />
            </SelectTrigger>
            <SelectContent>
              {suppliers.map((supplier) => (
                <SelectItem key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="status">Status</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({...formData, status: value})}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="edit_order_date">Order Date</Label>
          <Input
            id="edit_order_date"
            type="date"
            value={formData.order_date}
            onChange={(e) => setFormData({...formData, order_date: e.target.value})}
            data-testid="edit-po-order-date-input"
          />
        </div>

        <div>
          <Label htmlFor="expected_delivery_date">Expected Delivery Date</Label>
          <Input
            id="expected_delivery_date"
            type="date"
            value={formData.expected_delivery_date}
            onChange={(e) => setFormData({...formData, expected_delivery_date: e.target.value})}
          />
        </div>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50">
        <div>
          <Label htmlFor="po-edit-purchase-type" className="font-medium text-gray-700">GST Bill Available</Label>
          <p className="text-xs text-gray-500">On = supplier gave a proper GST invoice (ITC claimable, tax added to total). Off = local/unbilled purchase.</p>
        </div>
        <Switch
          id="po-edit-purchase-type"
          data-testid="po-edit-purchase-type-switch"
          checked={purchaseType === 'gst_bill'}
          onCheckedChange={(v) => setPurchaseType(v ? 'gst_bill' : 'non_gst')}
        />
      </div>

      {/* Order Items */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <Label>Order Items</Label>
          <Button type="button" onClick={addItem} variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Item
          </Button>
        </div>

        {orderItems.length === 0 ? (
          <Card className="p-6 text-center text-gray-500">
            <Package className="h-8 w-8 mx-auto mb-2" />
            <p>No items added yet. Click "Add Item" to get started.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {orderItems.map((item, index) => (
              <Card key={index} className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-start">
                  <div className="md:col-span-2 space-y-2">
                    <Label>Part Name</Label>
                    <Input
                      value={item.part_name || ''}
                      onChange={(e) => updateItem(index, 'part_name', e.target.value)}
                      placeholder="Part name"
                      data-testid={`po-item-name-${index}`}
                    />
                    <Input
                      value={item.part_number || ''}
                      onChange={(e) => updateItem(index, 'part_number', e.target.value)}
                      placeholder="OE / Part no."
                      className="text-xs"
                      data-testid={`po-item-oe-${index}`}
                    />
                    <Select
                      value={item.part_id || ''}
                      onValueChange={(value) => updateItem(index, 'part_id', value)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Or pick from inventory" />
                      </SelectTrigger>
                      <SelectContent>
                        {parts.map((part) => (
                          <SelectItem key={part.id} value={part.id}>
                            {part.part_name} - {part.part_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(() => {
                      const selPart = parts.find((p) => p.id === item.part_id);
                      const pbrands = (selPart && selPart.brands) || [];
                      if (pbrands.length === 0) return null;
                      return (
                        <Select
                          value={item.brand_id || ''}
                          onValueChange={(value) => updateItem(index, 'brand_id', value)}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`po-item-brand-select-${index}`}>
                            <SelectValue placeholder="Select brand">
                              {(pbrands.find((b) => b.id === item.brand_id)?.brand_name) || item.brand_name || 'Select brand'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {pbrands.map((b) => (
                              <SelectItem key={b.id} value={b.id}>
                                {b.brand_name}{b.part_number ? ` — ${b.part_number}` : ''} · Stock {b.stock_quantity ?? 0}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    })()}
                  </div>

                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                    />
                  </div>

                  <div>
                    <Label>Unit Cost (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.unit_cost}
                      onChange={(e) => updateItem(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                    />
                  </div>

                  <div>
                    <Label>Total</Label>
                    <Input
                      value={`₹${(item.total_cost || 0).toFixed(2)}`}
                      readOnly
                      className="bg-gray-50"
                    />
                  </div>

                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}

            {/* Order Total */}
            <Card className="p-4 bg-gray-50">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal:</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                {purchaseType === 'gst_bill' && (
                  <div className="flex justify-between text-sm text-gray-600" data-testid="po-edit-estimated-tax">
                    <span>Estimated GST (Input Tax):</span>
                    <span>₹{tax.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-sm">
                  <Label htmlFor="edit_shipping_charge" className="text-gray-600">Shipping Charge (₹):</Label>
                  <Input
                    id="edit_shipping_charge"
                    type="number"
                    min="0"
                    step="0.01"
                    value={shippingCharge}
                    onChange={(e) => setShippingCharge(e.target.value)}
                    className="w-32 h-8 text-right"
                    data-testid="po-edit-shipping-charge-input"
                  />
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span data-testid="po-edit-total-amount">₹{total.toFixed(2)}</span>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          placeholder="Additional notes or requirements..."
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          rows={3}
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting || !formData.supplier_id || orderItems.length === 0}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isSubmitting ? 'Updating...' : 'Update Purchase Order'}
        </Button>
      </div>
    </form>
  );
};

// View Purchase Order Details Component

const ViewPurchaseOrderDetails = ({ order, onClose }) => {
  const subtotal = order.subtotal || 0;
  const tax = order.tax_amount || 0;
  const shipping = order.shipping_charge || 0;
  const total = order.total_amount || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-xl font-bold">{order.po_number}</h3>
          <p className="text-gray-600">Purchase Order Details</p>
        </div>
        <div className="text-right">
          <Badge 
            className={`${
              order.status === 'delivered' ? 'bg-green-100 text-green-700' :
              order.status === 'confirmed' || order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
              order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
              order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}
          >
            {order.status?.toUpperCase()}
          </Badge>
          
          {/* Workflow Progress Indicator */}
          <div className="mt-2 text-xs text-gray-500">
            {order.status === 'draft' && 'Ready to send'}
            {order.status === 'pending' && 'Sent to supplier'}
            {order.status === 'confirmed' && 'Confirmed by supplier'}
            {order.status === 'shipped' && 'In transit'}
            {order.status === 'delivered' && 'Received & Stock Updated'}
            {order.status === 'cancelled' && 'Order cancelled'}
          </div>
        </div>
      </div>

      {/* Order Information */}
      <Card className="p-4">
        <h4 className="font-semibold mb-3">Order Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Supplier</p>
            <p className="font-medium">{order.supplier_name || <span className="text-amber-600">Not assigned</span>}</p>
          </div>
          {order.job_card_number && (
            <div>
              <p className="text-gray-500">Job Card Ref.</p>
              <p className="font-medium">#{order.job_card_number}</p>
            </div>
          )}
          {order.vehicle_reference && (
            <div>
              <p className="text-gray-500">Vehicle Ref.</p>
              <p className="font-medium">{order.vehicle_reference}</p>
            </div>
          )}
          <div>
            <p className="text-gray-500">Order Date</p>
            <p className="font-medium">{new Date(order.order_date).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-gray-500">Expected Delivery</p>
            <p className="font-medium">
              {order.expected_delivery_date 
                ? new Date(order.expected_delivery_date).toLocaleDateString()
                : 'Not specified'
              }
            </p>
          </div>
          <div>
            <p className="text-gray-500">Created By</p>
            <p className="font-medium">{order.created_by || 'Unknown'}</p>
          </div>
        </div>

        {order.notes && (
          <div className="mt-4">
            <p className="text-gray-500">Notes</p>
            <p className="text-gray-700">{order.notes}</p>
          </div>
        )}
      </Card>

      {/* Order Items */}
      <Card className="p-4">
        <h4 className="font-semibold mb-3">Order Items</h4>
        <div className="space-y-3">
          {order.items?.map((item, index) => (
            <div key={index} className="flex justify-between items-center border-b pb-3">
              <div className="flex-1">
                <p className="font-medium">{item.part_name}</p>
                <p className="text-sm text-gray-500">
                  {item.brand_name ? <span className="font-medium text-gray-700">{item.brand_name}</span> : null}
                  {item.brand_name && item.part_number ? ' · ' : ''}
                  {item.part_number}
                </p>
              </div>
              <div className="text-right">
                <p className="font-medium">Qty: {item.quantity}</p>
                <p className="text-sm text-gray-500">₹{item.unit_cost} each</p>
                <p className="font-semibold">₹{item.total_cost.toFixed(2)}</p>
              </div>
            </div>
          )) || (
            <p className="text-gray-500">No items in this order</p>
          )}
        </div>

        {/* Totals */}
        <div className="mt-6 pt-4 border-t">
          {order.purchase_type && (
            <div className="flex items-center justify-between mb-3 text-sm">
              <span className="text-gray-600">Purchase Type:</span>
              <Badge className={order.purchase_type === 'non_gst' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'} data-testid="po-view-purchase-type-badge">
                {order.purchase_type === 'non_gst' ? 'Non-GST (Local)' : 'GST Bill Available'}
              </Badge>
            </div>
          )}
          {order.supplier_gstin && (
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Supplier GSTIN:</span>
              <span>{order.supplier_gstin}</span>
            </div>
          )}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal (Taxable Value):</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            {tax > 0 && (
              order.is_interstate ? (
                <div className="flex justify-between text-sm text-gray-600" data-testid="po-view-igst-row">
                  <span>IGST (Input Tax):</span>
                  <span>₹{(order.total_input_igst || 0).toFixed(2)}</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between text-sm text-gray-600" data-testid="po-view-cgst-row">
                    <span>CGST (Input Tax):</span>
                    <span>₹{(order.total_input_cgst || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600" data-testid="po-view-sgst-row">
                    <span>SGST (Input Tax):</span>
                    <span>₹{(order.total_input_sgst || 0).toFixed(2)}</span>
                  </div>
                </>
              )
            )}
            {shipping > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Shipping Charge:</span>
                <span>₹{shipping.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total:</span>
              <span>₹{total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Close Button */}
      <div className="flex justify-end">
        <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700">
          Close
        </Button>
      </div>
    </div>
  );
};

// Make Payment Form Component

const MakePaymentForm = ({ purchaseOrder, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    payment_date: new Date().toISOString().split('T')[0],
    bank_account_id: '',
    transaction_reference: '',
    bank_account: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [reversingId, setReversingId] = useState(null);

  const remainingAmount = (purchaseOrder.total_amount || 0) - (purchaseOrder.paid_amount || 0);

  // Fetch bank accounts when component loads
  useEffect(() => {
    fetchBankAccounts();
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await apiFetch(`${API}/purchase-orders/${purchaseOrder.id}/payments`);
      if (response.ok) {
        setPaymentHistory(await response.json());
      }
    } catch (error) {
      console.error('Error fetching PO payment history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleReversePayment = async (paymentId) => {
    if (!window.confirm('Reverse this payment? This will remove it from the Supplier Statement and roll back the amount paid on this order.')) {
      return;
    }
    setReversingId(paymentId);
    try {
      const response = await apiFetch(`${API}/purchase-orders/${purchaseOrder.id}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        toast.success('Payment reversed successfully');
        await fetchPaymentHistory();
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reverse payment');
      }
    } catch (error) {
      console.error('Error reversing PO payment:', error);
      toast.error('Failed to reverse payment. Please try again.');
    } finally {
      setReversingId(null);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await apiFetch(`${API}/bank-accounts`);
      if (response.ok) {
        const accounts = await response.json();
        setBankAccounts(accounts);
      } else {
        console.error('Failed to fetch bank accounts');
        setBankAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      setBankAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate amount
      const paymentAmount = parseFloat(formData.amount);
      if (!paymentAmount || paymentAmount <= 0) {
        toast.error('Please enter a valid payment amount');
        setIsSubmitting(false);
        return;
      }

      if (paymentAmount > remainingAmount) {
        toast.error(`Payment amount cannot exceed remaining amount: ₹${remainingAmount.toFixed(2)}`);
        setIsSubmitting(false);
        return;
      }

      // Validate payment method specific requirements
      if (formData.payment_method === 'bank_transfer' && !formData.bank_account_id) {
        toast.error('Please select a bank account for bank transfer');
        setIsSubmitting(false);
        return;
      }

      // Get selected bank account details
      const selectedBankAccount = bankAccounts.find(acc => acc.id === formData.bank_account_id);
      const bankAccountInfo = selectedBankAccount 
        ? `${selectedBankAccount.bank_name} - ${selectedBankAccount.account_number}`
        : formData.bank_account.trim() || null;

      const paymentData = {
        purchase_order_id: purchaseOrder.id,
        amount: paymentAmount,
        payment_method: formData.payment_method,
        payment_date: formData.payment_date ? new Date(formData.payment_date).toISOString() : null,
        transaction_reference: formData.transaction_reference.trim() || null,
        bank_account_id: formData.payment_method === 'bank_transfer' ? formData.bank_account_id : null,
        bank_account: bankAccountInfo,
        notes: formData.notes.trim() || null
      };

      const response = await apiFetch(`${API}/purchase-orders/${purchaseOrder.id}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(paymentData)
      });

      if (response.ok) {
        toast.success('Payment recorded successfully!');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Order Summary */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">{purchaseOrder.po_number}</h4>
        <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
          <div>Total Amount: ₹{(purchaseOrder.total_amount || 0).toLocaleString('en-IN')}</div>
          <div>Paid Amount: ₹{(purchaseOrder.paid_amount || 0).toLocaleString('en-IN')}</div>
          <div className="font-semibold col-span-2">
            Remaining: ₹{remainingAmount.toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      {/* Payment History — reverse a mistaken payment here so PO status,
          cash/bank and the Supplier Statement all stay in sync */}
      {!loadingHistory && paymentHistory.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-3" data-testid="po-payment-history">
          <h5 className="text-sm font-semibold text-gray-700 mb-2">Payment History</h5>
          <div className="space-y-2">
            {paymentHistory.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm bg-gray-50 rounded p-2" data-testid={`po-payment-row-${p.id}`}>
                <div>
                  <div className="font-medium text-gray-900">₹{(p.amount || 0).toLocaleString('en-IN')} — <span className="capitalize">{(p.payment_method || '').replace('_', ' ')}</span></div>
                  <div className="text-xs text-gray-500">
                    {p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-IN') : ''}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  disabled={reversingId === p.id}
                  onClick={() => handleReversePayment(p.id)}
                  data-testid={`reverse-po-payment-${p.id}`}
                >
                  {reversingId === p.id ? 'Reversing...' : 'Reverse'}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Payment Amount */}
        <div>
          <Label htmlFor="amount">Payment Amount (₹) *</Label>
          <Input
            id="amount"
            name="amount"
            type="number"
            step="0.01"
            min="0.01"
            max={remainingAmount}
            value={formData.amount}
            onChange={handleInputChange}
            placeholder={`Max: ₹${remainingAmount.toFixed(2)}`}
            required
            className="no-uppercase"
          />
        </div>

        {/* Payment Method */}
        <div>
          <Label htmlFor="payment_method">Payment Method *</Label>
          <select
            id="payment_method"
            name="payment_method"
            value={formData.payment_method}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 no-uppercase"
            required
          >
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cash">Cash Payment</option>
            <option value="credit">Credit Payment</option>
          </select>
        </div>

        {/* Bank Account Selection (for bank transfers) */}
        {formData.payment_method === 'bank_transfer' && (
          <div>
            <Label htmlFor="bank_account_id">Select Bank Account *</Label>
            {loading ? (
              <div className="text-sm text-gray-500">Loading bank accounts...</div>
            ) : bankAccounts.length === 0 ? (
              <div className="text-sm text-red-500">
                No bank accounts found. Please add a bank account first.
              </div>
            ) : (
              <select
                id="bank_account_id"
                name="bank_account_id"
                value={formData.bank_account_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 no-uppercase"
                required
              >
                <option value="">Choose bank account...</option>
                {bankAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.bank_name} - {account.account_number} 
                    {account.is_primary && ' (Primary)'}
                    {account.current_balance && ` - Balance: ₹${account.current_balance.toLocaleString('en-IN')}`}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Cash Payment Note */}
        {formData.payment_method === 'cash' && (
          <div className="bg-yellow-50 p-3 rounded-md">
            <div className="flex items-center">
              <span className="text-yellow-600 text-sm">
                💰 Cash payment will be recorded in your cash register
              </span>
            </div>
          </div>
        )}

        {/* Credit Payment Note */}
        {formData.payment_method === 'credit' && (
          <div className="bg-blue-50 p-3 rounded-md">
            <div className="flex items-center">
              <span className="text-blue-600 text-sm">
                💳 Credit payment will be recorded as payable in Credit Management system
                <br />
                <span className="font-medium">Supplier:</span> {purchaseOrder.supplier_name}
              </span>
            </div>
          </div>
        )}

        {/* Payment Date */}
        <div>
          <Label htmlFor="payment_date">Payment Date</Label>
          <Input
            id="payment_date"
            name="payment_date"
            type="date"
            value={formData.payment_date}
            onChange={handleInputChange}
            className="no-uppercase"
          />
        </div>

        {/* Transaction Reference (for bank transfers) */}
        {formData.payment_method === 'bank_transfer' && (
          <div>
            <Label htmlFor="transaction_reference">Transaction Reference</Label>
            <Input
              id="transaction_reference"
              name="transaction_reference"
              value={formData.transaction_reference}
              onChange={handleInputChange}
              placeholder="Bank reference number or transaction ID"
              className="no-uppercase"
            />
          </div>
        )}

        {/* Bank Account (for bank transfers) */}
        {formData.payment_method === 'bank_transfer' && (
          <div>
            <Label htmlFor="bank_account">Bank Account</Label>
            <Input
              id="bank_account"
              name="bank_account"
              value={formData.bank_account}
              onChange={handleInputChange}
              placeholder="Account number or bank details"
              className="no-uppercase"
            />
          </div>
        )}

        {/* Notes */}
        <div>
          <Label htmlFor="notes">Notes</Label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Additional payment notes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none no-uppercase"
            rows="3"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isSubmitting ? 'Recording...' : 'Record Payment'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Purchase Orders Section Component

const PurchaseOrdersSection = ({ onUpdate }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    total_orders: 0,
    pending_orders: 0,
    confirmed_orders: 0,
    delivered_orders: 0,
    total_value: 0,
    pending_value: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showConfirmReceiveDialog, setShowConfirmReceiveDialog] = useState(false);
  const [orderToReceive, setOrderToReceive] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [orderToPayFor, setOrderToPayFor] = useState(null);

  useEffect(() => {
    fetchPurchaseOrders();
    fetchSuppliers();
    fetchParts();
    fetchStatistics();
  }, []);

  const fetchPurchaseOrders = async () => {
    try {
      const response = await apiFetch(`${API}/purchase-orders`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setPurchaseOrders(data || []);
    } catch (error) {
      console.error('Failed to fetch purchase orders:', error);
      setPurchaseOrders([]);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await apiFetch(`${API}/suppliers`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setSuppliers(data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      setSuppliers([]);
    }
  };

  const fetchParts = async () => {
    try {
      const response = await apiFetch(`${API}/parts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setParts(data || []);
    } catch (error) {
      console.error('Failed to fetch parts:', error);
      setParts([]);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await apiFetch(`${API}/purchase-orders/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setStatistics(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      setLoading(false);
    }
  };

  // Derive payment summary for any PO. We accept multiple legacy shapes:
  // - explicit payment_status field ('paid' | 'partial' | 'pending')
  // - or compute from total_amount vs paid_amount
  const getPaymentInfo = (order) => {
    const total = Number(order.total_amount || 0);
    const paid = Number(order.paid_amount || 0);
    const balance = Math.max(0, total - paid);
    let status;
    if (order.payment_status) {
      status = order.payment_status;
    } else if (paid <= 0.01) {
      status = total > 0 ? 'pending' : 'paid';
    } else if (balance <= 0.01) {
      status = 'paid';
    } else {
      status = 'partial';
    }
    return { total, paid, balance, status };
  };

  // Aggregate payment numbers across all POs (used by the new dashboard cards)
  const paymentStats = (() => {
    let totalPaid = 0;
    let supplierOutstanding = 0;
    let pendingCount = 0;
    let partialCount = 0;
    let paidCount = 0;
    for (const o of purchaseOrders) {
      if (o.status === 'cancelled') continue;
      const p = getPaymentInfo(o);
      totalPaid += p.paid;
      supplierOutstanding += p.balance;
      if (p.status === 'paid') paidCount += 1;
      else if (p.status === 'partial') partialCount += 1;
      else if (p.status === 'pending') pendingCount += 1;
    }
    return { totalPaid, supplierOutstanding, pendingCount, partialCount, paidCount };
  })();

  const filteredOrders = purchaseOrders.filter(order => {
    const matchesSearch = 
      order.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    const matchesPayment = paymentStatusFilter === 'all' || (() => {
      const p = getPaymentInfo(order);
      if (paymentStatusFilter === 'outstanding') return p.balance > 0.01 && order.status !== 'cancelled';
      return p.status === paymentStatusFilter;
    })();
    
    return matchesSearch && matchesStatus && matchesPayment;
  }).sort((a, b) => {
    // Newest first
    const aKey = a.created_at || a.order_date || a.po_number || '';
    const bKey = b.created_at || b.order_date || b.po_number || '';
    return String(bKey).localeCompare(String(aKey));
  });

  // Purchase Order Statement: Export CSV / Print PDF (respects current search/status/payment filters)
  const buildPOStatementRows = () => filteredOrders.map(order => {
    const p = getPaymentInfo(order);
    return {
      po_number: order.po_number || '\u2014',
      date: (order.order_date || (order.created_at || '').slice(0, 10) || '\u2014').slice(0, 10),
      supplier_name: order.supplier_name || '\u2014',
      status: order.status || '\u2014',
      total_amount: p.total,
      paid_amount: p.paid,
      balance_due: p.balance,
    };
  });

  const formatMoney = (amt) => `\u20b9${Number(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleExportPOStatementCSV = () => {
    const rows = buildPOStatementRows();
    exportRowsToCSV(
      `purchase-order-statement-${toLocalISODate(new Date())}`,
      ['PO No', 'Date', 'Supplier', 'Status', 'Total Amount', 'Paid Amount', 'Balance Due'],
      rows.map(r => [r.po_number, r.date, r.supplier_name, r.status, r.total_amount, r.paid_amount, r.balance_due])
    );
  };

  const handlePrintPOStatement = () => {
    const rows = buildPOStatementRows();
    const totals = rows.reduce((acc, r) => ({
      total: acc.total + r.total_amount, paid: acc.paid + r.paid_amount, balance: acc.balance + r.balance_due
    }), { total: 0, paid: 0, balance: 0 });
    const tableRows = rows.map(r => `<tr>
      <td>${r.po_number}</td><td>${r.date}</td><td>${r.supplier_name}</td>
      <td style="text-transform:capitalize">${r.status}</td>
      <td style="text-align:right">${formatMoney(r.total_amount)}</td>
      <td style="text-align:right">${formatMoney(r.paid_amount)}</td>
      <td style="text-align:right">${formatMoney(r.balance_due)}</td>
    </tr>`).join('');
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Purchase Order Statement</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#111} h1{font-size:20px;margin-bottom:4px} p{color:#555;font-size:12px;margin-top:0} table{width:100%;border-collapse:collapse;margin-top:16px} th,td{border:1px solid #ddd;padding:6px 8px;text-align:left;font-size:12px} th{background:#f3f4f6} tfoot td{font-weight:bold;background:#f9fafb}</style>
      </head><body>
      <h1>Purchase Order Statement</h1>
      <p>Generated on ${new Date().toLocaleString('en-IN')} \u2014 ${rows.length} order(s)</p>
      <table><thead><tr><th>PO No</th><th>Date</th><th>Supplier</th><th>Status</th><th>Total Amount</th><th>Paid Amount</th><th>Balance Due</th></tr></thead>
      <tbody>${tableRows}</tbody>
      <tfoot><tr><td colspan="4">TOTAL</td><td style="text-align:right">${formatMoney(totals.total)}</td><td style="text-align:right">${formatMoney(totals.paid)}</td><td style="text-align:right">${formatMoney(totals.balance)}</td></tr></tfoot>
      </table>
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowViewDialog(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setShowEditDialog(true);
  };

  const handleDeleteOrder = async (order) => {
    // Create different confirmation messages based on order status
    let confirmMessage;
    if (order.status === 'draft') {
      confirmMessage = `Are you sure you want to delete Purchase Order ${order.po_number}?\n\nThis action cannot be undone.`;
    } else if (order.status === 'delivered') {
      confirmMessage = `⚠️ WARNING: You are about to delete a COMPLETED purchase order!\n\nPO: ${order.po_number}\nSupplier: ${order.supplier_name}\nAmount: ₹${order.total_amount?.toLocaleString('en-IN') || 0}\nStatus: ${order.status.toUpperCase()}\n\nThis will:\n• Remove the purchase order permanently\n• Not affect already updated stock levels\n• Cannot be undone\n\nAre you absolutely sure you want to proceed?`;
    } else {
      confirmMessage = `⚠️ CAUTION: You are about to delete a ${order.status.toUpperCase()} purchase order!\n\nPO: ${order.po_number}\nSupplier: ${order.supplier_name}\nAmount: ₹${order.total_amount?.toLocaleString('en-IN') || 0}\n\nThis action cannot be undone. Are you sure you want to proceed?`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await apiFetch(`${API}/purchase-orders/${order.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        toast.success(`Purchase Order ${order.po_number} deleted successfully!`);
        await fetchPurchaseOrders();
        await fetchStatistics();
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete purchase order');
      }
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      toast.error('Failed to delete purchase order');
    }
  };

  // Send Purchase Order (Draft → Pending)
  const handleSendOrder = async (orderId) => {
    try {
      const response = await apiFetch(`${API}/purchase-orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          status: 'pending'
        })
      });

      if (response.ok) {
        toast.success('Purchase order sent to supplier!');
        fetchPurchaseOrders();
        fetchStatistics();
      } else {
        toast.error('Failed to send purchase order');
      }
    } catch (error) {
      console.error('Error sending purchase order:', error);
      toast.error('Failed to send purchase order');
    }
  };

  // Mark as Received and Update Stock
  const handleMarkAsReceived = async (orderId) => {
    const order = purchaseOrders.find(o => o.id === orderId);
    setOrderToReceive(order);
    setShowConfirmReceiveDialog(true);
  };

  const confirmMarkAsReceived = async () => {
    if (!orderToReceive) return;
    
    try {
      if (!orderToReceive || !orderToReceive.items) {
        toast.error('Order not found or has no items');
        return;
      }

      // Update purchase order status to delivered.
      // The backend automatically adds each item's quantity to the corresponding
      // part's stock when the status transitions to "delivered" — do NOT update
      // stock again from the frontend or it will be added twice.
      const orderUpdateResponse = await apiFetch(`${API}/purchase-orders/${orderToReceive.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          status: 'delivered'
        })
      });

      if (!orderUpdateResponse.ok) {
        toast.error('Failed to mark order as received');
        return;
      }

      toast.success('Purchase order received! Parts stock updated successfully.');

      fetchPurchaseOrders();
      fetchStatistics();
      if (onUpdate) onUpdate(); // Refresh parts in parent component
      
      // Close dialog
      setShowConfirmReceiveDialog(false);
      setOrderToReceive(null);

    } catch (error) {
      console.error('Error marking order as received:', error);
      toast.error('Failed to mark order as received');
    }
  };

  const handleOrderAdded = () => {
    fetchPurchaseOrders();
    fetchStatistics();
    setShowAddDialog(false);
    if (onUpdate) onUpdate();
  };

  const handleOrderUpdated = () => {
    fetchPurchaseOrders();
    fetchStatistics();
    setShowEditDialog(false);
    setSelectedOrder(null);
    if (onUpdate) onUpdate();
  };

  const handleMakePayment = (order) => {
    setOrderToPayFor(order);
    setShowPaymentDialog(true);
  };

  const handlePaymentSuccess = () => {
    fetchPurchaseOrders();
    fetchStatistics();
    setShowPaymentDialog(false);
    setOrderToPayFor(null);
    if (onUpdate) onUpdate();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Purchase Orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-gray-600">Manage parts procurement and supplier orders</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportPOStatementCSV} variant="outline" data-testid="po-statement-export-csv-btn">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button onClick={handlePrintPOStatement} variant="outline" data-testid="po-statement-print-btn">
            <FileText className="mr-2 h-4 w-4" />
            Print Statement
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="mr-2 h-4 w-4" />
            Create Purchase Order
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.total_orders}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Orders</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.pending_orders}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Confirmed Orders</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.confirmed_orders}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Delivered Orders</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.delivered_orders}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₹{statistics.total_value?.toLocaleString('en-IN')}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Value</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₹{statistics.pending_value?.toLocaleString('en-IN')}</div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow border-emerald-200"
          onClick={() => setPaymentStatusFilter('paid')}
          data-testid="po-card-total-paid"
          title="Click to filter to fully-paid POs"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Paid to Suppliers</CardTitle>
            <CheckCircle className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">₹{paymentStats.totalPaid.toLocaleString('en-IN')}</div>
            <div className="text-xs text-gray-500 mt-1">{paymentStats.paidCount} paid POs</div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow border-red-200"
          onClick={() => setPaymentStatusFilter('outstanding')}
          data-testid="po-card-supplier-outstanding"
          title="Click to filter to POs with outstanding payment"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Supplier Outstanding</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">₹{paymentStats.supplierOutstanding.toLocaleString('en-IN')}</div>
            <div className="text-xs text-gray-500 mt-1">{paymentStats.pendingCount + paymentStats.partialCount} POs to pay</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by PO number, supplier, or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="po-payment-status-filter">
              <SelectValue placeholder="All Payment Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payment Status</SelectItem>
              <SelectItem value="paid">Paid in Full</SelectItem>
              <SelectItem value="partial">Partially Paid</SelectItem>
              <SelectItem value="pending">Unpaid</SelectItem>
              <SelectItem value="outstanding">Outstanding (Unpaid + Partial)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Purchase Orders List */}
      <div className="grid gap-4">
        {filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <CreditCard className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Purchase Orders Found</h3>
              <p className="text-gray-500 text-center mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? "Try adjusting your search or filter criteria"
                  : "Create your first purchase order to get started"
                }
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="mr-2 h-4 w-4" />
                  Create First Purchase Order
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredOrders.map((order) => (
            <Card key={order.id} className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-lg font-semibold">{order.po_number}</h3>
                    <Badge 
                      variant="outline" 
                      className={`capitalize ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                        order.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {order.status}
                    </Badge>
                    {order.job_card_number && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        Job Card #{order.job_card_number}
                      </Badge>
                    )}
                    <span className="text-sm text-gray-500">
                      {new Date(order.order_date).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-gray-500">Supplier</p>
                      <p className="font-medium">{order.supplier_name || <span className="text-amber-600">Not assigned</span>}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Items</p>
                      <p className="font-medium">{order.items?.length || 0} items</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Total Amount</p>
                      <p className="font-medium text-green-600">₹{order.total_amount?.toLocaleString('en-IN')}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Payment Status</p>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline" 
                          className={`capitalize ${
                            order.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                            order.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}
                        >
                          {order.payment_status || 'pending'}
                        </Badge>
                        {order.payment_status === 'partial' && (
                          <span className="text-xs text-gray-500">
                            ₹{(order.paid_amount || 0).toLocaleString('en-IN')} / ₹{order.total_amount?.toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-500">Expected Delivery</p>
                      <p className="font-medium">
                        {order.expected_delivery_date 
                          ? new Date(order.expected_delivery_date).toLocaleDateString()
                          : 'Not specified'
                        }
                      </p>
                    </div>
                  </div>

                  {order.notes && (
                    <div className="text-sm">
                      <p className="text-gray-500">Notes</p>
                      <p className="text-gray-700">{order.notes}</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 ml-4">
                  {/* All orders can be viewed */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewOrder(order)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>

                  {/* Editable until cancelled. Delivered orders can only be re-opened by an admin. */}
                  {order.status !== 'cancelled' && (order.status !== 'delivered' || isAdmin) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditOrder(order)}
                      data-testid={`po-edit-btn-${order.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}

                  {/* Draft orders can be sent */}
                  {order.status === 'draft' && (
                    <Button
                      size="sm"
                      onClick={() => handleSendOrder(order.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid={`po-send-btn-${order.id}`}
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Send Order
                    </Button>
                  )}
                  
                  {/* Delete button for all orders (with appropriate warnings) */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteOrder(order)}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    <Trash className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                  
                  {/* Pending/Confirmed orders can be marked as received */}
                  {(order.status === 'pending' || order.status === 'confirmed' || order.status === 'shipped') && (
                    <Button
                      size="sm"
                      onClick={() => handleMarkAsReceived(order.id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Mark Received
                    </Button>
                  )}
                  
                  {/* Payment button for orders that are not fully paid */}
                  {order.payment_status !== 'paid' && order.total_amount > 0 && (
                    <Button
                      size="sm"
                      onClick={() => handleMakePayment(order)}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <CreditCard className="h-4 w-4 mr-1" />
                      {order.payment_status === 'partial' ? 'Pay Balance' : 'Make Payment'}
                    </Button>
                  )}
                  
                  {/* Delivered orders show completion badge */}
                  {order.status === 'delivered' && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                      ✅ Completed
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Add Purchase Order Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Purchase Order</DialogTitle>
            <DialogDescription>
              Create a new purchase order for parts from suppliers
            </DialogDescription>
          </DialogHeader>
          {showAddDialog && (
            <AddPurchaseOrderForm
              suppliers={suppliers}
              parts={parts}
              onSuccess={handleOrderAdded}
              onCancel={() => setShowAddDialog(false)}
              onPartCreated={fetchParts}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Purchase Order Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Purchase Order</DialogTitle>
            <DialogDescription>
              Update the purchase order details
            </DialogDescription>
          </DialogHeader>
          {showEditDialog && selectedOrder && (
            <EditPurchaseOrderForm
              order={selectedOrder}
              suppliers={suppliers}
              parts={parts}
              onSuccess={handleOrderUpdated}
              onCancel={() => {
                setShowEditDialog(false);
                setSelectedOrder(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Purchase Order Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
            <DialogDescription>
              Complete purchase order information
            </DialogDescription>
          </DialogHeader>
          {showViewDialog && selectedOrder && (
            <ViewPurchaseOrderDetails
              order={selectedOrder}
              onClose={() => {
                setShowViewDialog(false);
                setSelectedOrder(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Receive Order Dialog */}
      <Dialog open={showConfirmReceiveDialog} onOpenChange={setShowConfirmReceiveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Order Receipt</DialogTitle>
            <DialogDescription>
              Mark this purchase order as received and update parts inventory
            </DialogDescription>
          </DialogHeader>
          
          {orderToReceive && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">{orderToReceive.po_number}</h4>
                <p className="text-sm text-blue-700 mb-2">
                  Supplier: {orderToReceive.supplier_name}
                </p>
                <p className="text-sm text-blue-700 mb-3">
                  Total: ₹{orderToReceive.total_amount?.toFixed(2)}
                </p>
                
                <div className="text-sm">
                  <p className="font-medium text-blue-900 mb-2">Items to add to stock:</p>
                  <div className="max-h-32 overflow-y-auto">
                    {orderToReceive.items?.map((item, index) => (
                      <div key={index} className="flex justify-between text-blue-700">
                        <span>{item.part_name}</span>
                        <span>+{item.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      This action will:
                    </p>
                    <ul className="text-xs text-yellow-700 mt-1 ml-2">
                      <li>• Mark the purchase order as delivered</li>
                      <li>• Add received quantities to parts inventory</li>
                      <li>• Cannot be undone</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowConfirmReceiveDialog(false);
                    setOrderToReceive(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmMarkAsReceived}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Make Payment</DialogTitle>
            <DialogDescription>
              Record a payment for this purchase order
            </DialogDescription>
          </DialogHeader>
          
          {orderToPayFor && (
            <MakePaymentForm
              purchaseOrder={orderToPayFor}
              onSuccess={handlePaymentSuccess}
              onCancel={() => {
                setShowPaymentDialog(false);
                setOrderToPayFor(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Credit Management Form Components ============

// Add Credit Account Form Component
// [Credit/Statement components extracted to pages/AccountingSection.jsx]

// ============ Engine Procedures Section ============

export { PurchaseOrdersSection };
